import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { GraduationCap } from "lucide-react";

export default function Auth() {
  const [params] = useSearchParams();
  const initial = params.get("mode") === "signup" ? "signup" : "login";
  const [mode, setMode] = useState(initial);
  const { login, signup } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    email: "", password: "", parent_name: "", student_name: "", grade: "K",
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        await signup(form);
        toast.success("Account created!");
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="auth-page" className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="blob -top-20 -left-20 w-80 h-80 bg-sky-200" />
      <div className="blob bottom-0 -right-20 w-96 h-96 bg-amber-200" />

      <div className="relative w-full max-w-md bg-white rounded-3xl p-10 shadow-[0_20px_60px_rgba(2,132,199,0.12)] border border-slate-100 animate-pop">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-sky-400 flex items-center justify-center">
            <GraduationCap className="text-white" strokeWidth={2.5}/>
          </div>
          <div>
            <div className="font-black text-2xl">{mode === "login" ? "Welcome back" : "Let's begin"}</div>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">The Haddis Academy LLC</div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5">
          {mode === "signup" && (
            <>
              <div>
                <Label htmlFor="parent_name" className="text-xs font-bold uppercase tracking-widest text-slate-500">Parent name</Label>
                <Input id="parent_name" data-testid="signup-parent-name" required value={form.parent_name}
                  onChange={(e)=>setForm({...form, parent_name:e.target.value})}
                  className="mt-2 rounded-xl h-12 border-slate-200" placeholder="Jane Doe" />
              </div>
              <div>
                <Label htmlFor="student_name" className="text-xs font-bold uppercase tracking-widest text-slate-500">Student name</Label>
                <Input id="student_name" data-testid="signup-student-name" required value={form.student_name}
                  onChange={(e)=>setForm({...form, student_name:e.target.value})}
                  className="mt-2 rounded-xl h-12 border-slate-200" placeholder="Alex Doe" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Grade</Label>
                <Select value={form.grade} onValueChange={(v)=>setForm({...form, grade:v})}>
                  <SelectTrigger data-testid="signup-grade" className="mt-2 rounded-xl h-12 border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["K","1","2","3","4","5"].map(g => (
                      <SelectItem key={g} value={g}>{g === "K" ? "Kindergarten" : `Grade ${g}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div>
            <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</Label>
            <Input id="email" data-testid="auth-email" type="email" required value={form.email}
              onChange={(e)=>setForm({...form, email:e.target.value})}
              className="mt-2 rounded-xl h-12 border-slate-200" placeholder="parent@email.com" />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-slate-500">Password</Label>
            <Input id="password" data-testid="auth-password" type="password" required minLength={6} value={form.password}
              onChange={(e)=>setForm({...form, password:e.target.value})}
              className="mt-2 rounded-xl h-12 border-slate-200" placeholder="••••••" />
          </div>

          <Button type="submit" disabled={busy} data-testid="auth-submit"
            className="w-full rounded-full h-12 bg-sky-400 hover:bg-sky-500 text-white font-bold shadow-lg shadow-sky-200">
            {busy ? "Please wait..." : mode === "login" ? "Log in" : "Create account"}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-slate-500">
          {mode === "login" ? (
            <span>New here? <button data-testid="switch-signup" onClick={()=>setMode("signup")} className="font-bold text-sky-600 underline">Create account</button></span>
          ) : (
            <span>Already have an account? <button data-testid="switch-login" onClick={()=>setMode("login")} className="font-bold text-sky-600 underline">Log in</button></span>
          )}
        </div>
        <div className="mt-2 text-center text-xs text-slate-400"><Link to="/">← Back to home</Link></div>
      </div>
    </div>
  );
}
